import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  CATEGORIAS_CIRCULARIZACAO,
  CategoriaCircularizacao,
  IAlertaInconsistencia,
  IContatoCircularizacao,
  IHistoricoAjusteCircularizacao,
  IIndicadoresCircularizacao,
  IRegistroCircularizacao,
} from './interfaces/ICircularizacao';
import { CircularizacaoService } from './services/CircularizacaoService';
import { slugParaCategoria } from './utils/categoriaSlug';
import { JUSTIFICATIVA_PADRAO_NAO_APLICAVEL } from './utils/justificativasPadrao';

import RegistroCircularizacaoRow from './components/RegistroCircularizacaoRow';
import IncluirEmpresaModal from './components/IncluirEmpresaModal';
import ExcluirEmpresaModal from './components/ExcluirEmpresaModal';
import EditarEmpresaModal from './components/EditarEmpresaModal';
import EditarJustificativaExclusaoModal from './components/EditarJustificativaExclusaoModal';
import JustificativaModal from './components/JustificativaModal';
import ContatoFormModal from './components/ContatoFormModal';
import PlanejamentoLoteModal from './components/PlanejamentoLoteModal';
import PlanejamentoCircularizacaoTable from './components/PlanejamentoCircularizacaoTable';
import ExclusaoEmLoteModal from './components/ExclusaoEmLoteModal';
import ModeloCartaPreviewModal from './components/ModeloCartaPreviewModal';
import ImpressaoEmLoteModal from './components/ImpressaoEmLoteModal';
import HistoricoAjustesModal from './components/HistoricoAjustesModal';
import AlertasInconsistenciaPanel from './components/AlertasInconsistenciaPanel';
import CriteriosConclusaoPanel from './components/CriteriosConclusaoPanel';
import CartaDetalheModal from './components/CartaDetalheModal';
import TemplatesModal from './components/TemplatesModal';
import PoliticaCobrancaModal from './components/PoliticaCobrancaModal';
import RespostasPendentesModal from './components/RespostasPendentesModal';

import './styles/circularizacao.css';

type Aba = 'ATIVAS' | 'EXCLUIDAS' | 'PLANEJAMENTO';

interface LocationState {
  empresaId?: number;
  periodo?: string;
  empresaNome?: string;
  periodoDescricao?: string;
  // Seção 2 — navegação direta a partir do aviso de pendências na sidebar: já abre direto na
  // aba de Planejamento desta categoria.
  abaInicial?: Aba;
}

const CategoriaCircularizacaoPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const { categoria: categoriaSlug } = useParams<{ categoria: string }>();
  const categoria = slugParaCategoria(categoriaSlug);
  const categoriaInfo = CATEGORIAS_CIRCULARIZACAO.find((c) => c.value === categoria);

  const empresaId = location.state?.empresaId;
  const periodo = location.state?.periodo;
  const esteiraId = periodo ? Number(periodo) : undefined;
  const empresaNome = location.state?.empresaNome ?? 'Empresa Auditada';
  const periodoDescricao = location.state?.periodoDescricao ?? '—';

  const [aba, setAba] = useState<Aba>(location.state?.abaInicial ?? 'ATIVAS');
  const [ativos, setAtivos] = useState<IRegistroCircularizacao[]>([]);
  const [excluidos, setExcluidos] = useState<IRegistroCircularizacao[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [indicadores, setIndicadores] = useState<IIndicadoresCircularizacao | undefined>();
  const [alertas, setAlertas] = useState<IAlertaInconsistencia[]>([]);
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [justificativaNaoAplicavel, setJustificativaNaoAplicavel] = useState<string | undefined>();

  const [toast, setToast] = useState('');
  const mostrarToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const [mostrarIncluir, setMostrarIncluir] = useState(false);
  const [salvandoIncluir, setSalvandoIncluir] = useState(false);
  const [registroParaExcluir, setRegistroParaExcluir] = useState<IRegistroCircularizacao | undefined>();
  const [excluindo, setExcluindo] = useState(false);
  const [registroParaEditar, setRegistroParaEditar] = useState<IRegistroCircularizacao | undefined>();
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [registroParaJustificativa, setRegistroParaJustificativa] = useState<IRegistroCircularizacao | undefined>();
  const [registroParaContatos, setRegistroParaContatos] = useState<IRegistroCircularizacao | undefined>();
  const [contatoEmEdicao, setContatoEmEdicao] = useState<IContatoCircularizacao | 'novo' | undefined>();
  const [salvandoContato, setSalvandoContato] = useState(false);
  const [registroParaPlanejamento, setRegistroParaPlanejamento] = useState<IRegistroCircularizacao | undefined>();
  const [salvandoPlanejamento, setSalvandoPlanejamento] = useState(false);
  const [registroParaPreview, setRegistroParaPreview] = useState<IRegistroCircularizacao | undefined>();
  const [idsPreparandoCarta, setIdsPreparandoCarta] = useState<Set<string>>(new Set());
  const [mostrarPrazoLote, setMostrarPrazoLote] = useState(false);
  const [aplicandoLote, setAplicandoLote] = useState(false);
  const [mostrarExclusaoLote, setMostrarExclusaoLote] = useState(false);
  const [excluindoLote, setExcluindoLote] = useState(false);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [historico, setHistorico] = useState<IHistoricoAjusteCircularizacao[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [mostrarNaoAplicavel, setMostrarNaoAplicavel] = useState(false);
  const [justificativaNaoAplicavelForm, setJustificativaNaoAplicavelForm] = useState(JUSTIFICATIVA_PADRAO_NAO_APLICAVEL);
  const [registroParaJustificativaExclusao, setRegistroParaJustificativaExclusao] = useState<IRegistroCircularizacao | undefined>();
  const [salvandoJustificativaExclusao, setSalvandoJustificativaExclusao] = useState(false);
  const [mostrarImpressaoLote, setMostrarImpressaoLote] = useState(false);
  const [registroParaCarta, setRegistroParaCarta] = useState<IRegistroCircularizacao | undefined>();
  const [mostrarTemplates, setMostrarTemplates] = useState(false);
  const [mostrarPoliticaCobranca, setMostrarPoliticaCobranca] = useState(false);
  const [mostrarRespostasPendentes, setMostrarRespostasPendentes] = useState(false);

  const carregarTudo = async () => {
    if (!esteiraId || !categoria) return;
    setCarregando(true);
    const [listaAtivos, listaExcluidos, ind, listaAlertas, justNaoAplicavel] = await Promise.all([
      CircularizacaoService.listarAtivos(esteiraId, categoria),
      CircularizacaoService.listarExcluidos(esteiraId, categoria),
      CircularizacaoService.obterIndicadores(esteiraId, categoria),
      CircularizacaoService.listarAlertasInconsistencia(esteiraId, categoria),
      CircularizacaoService.obterJustificativaNaoAplicavel(esteiraId, categoria),
    ]);
    setAtivos(listaAtivos);
    setExcluidos(listaExcluidos);
    setIndicadores(ind);
    setAlertas(listaAlertas);
    setJustificativaNaoAplicavel(justNaoAplicavel);
    setCarregando(false);
  };

  useEffect(() => {
    carregarTudo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esteiraId, categoria]);

  if (!categoria || !categoriaInfo) {
    return (
      <div className='auditoria-page'>
        <div className='circ-card'>
          Categoria de circularização desconhecida.{' '}
          <button type='button' className='btn btn-sm btn-light' onClick={() => history.push('/auditoria/circularizacao')}>
            Voltar para a Central
          </button>
        </div>
      </div>
    );
  }

  if (!empresaId || !esteiraId) {
    return (
      <div className='auditoria-page'>
        <div className='circ-card'>
          Selecione uma Empresa e um Período na Central de Circularizações antes de gerenciar uma categoria.{' '}
          <button type='button' className='btn btn-sm btn-light' onClick={() => history.push('/auditoria/circularizacao')}>
            Voltar para a Central
          </button>
        </div>
      </div>
    );
  }

  const toggleSelecionado = (id: string) => {
    setSelecionados((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const selecionarTodos = () => {
    setSelecionados((prev) => (prev.size === ativos.length ? new Set() : new Set(ativos.map((r) => r.id))));
  };

  // `continuar=true` (seção 12.4 — inclusão em lote) mantém o modal aberto para o auditor
  // acrescentar o próximo destinatário em sequência.
  const confirmarIncluir = async (dados: Parameters<typeof CircularizacaoService.incluirEmpresa>[1], continuar: boolean) => {
    setSalvandoIncluir(true);
    await CircularizacaoService.incluirEmpresa(esteiraId, dados);
    setSalvandoIncluir(false);
    if (!continuar) {
      setMostrarIncluir(false);
      mostrarToast('Empresa incluída na circularização.');
    }
    carregarTudo();
  };

  const confirmarExcluir = async (justificativa: string) => {
    if (!registroParaExcluir) return;
    setExcluindo(true);
    const resultado = await CircularizacaoService.excluirEmpresa(esteiraId, registroParaExcluir.id, justificativa);
    setExcluindo(false);
    setRegistroParaExcluir(undefined);
    mostrarToast(resultado.mensagem);
    carregarTudo();
  };

  const confirmarRestaurar = async (registro: IRegistroCircularizacao) => {
    const resultado = await CircularizacaoService.restaurarEmpresa(esteiraId, registro.id);
    mostrarToast(resultado.mensagem);
    carregarTudo();
  };

  const confirmarEditar = async (
    dados: { razaoSocialExibicao: string; nomeFantasia?: string; cnpj: string; categoria: CategoriaCircularizacao },
    justificativa: string
  ) => {
    if (!registroParaEditar) return;
    setSalvandoEdicao(true);
    const resultado = await CircularizacaoService.editarEmpresa(esteiraId, registroParaEditar.id, dados, justificativa);
    setSalvandoEdicao(false);
    setRegistroParaEditar(undefined);
    mostrarToast(resultado.mensagem);
    carregarTudo();
  };

  // Seção 12.3 — "Edição rápida": renomear sem abrir o modal completo, direto na linha da lista.
  const confirmarRenomeioRapido = async (registro: IRegistroCircularizacao, novoNome: string, justificativa: string) => {
    const resultado = await CircularizacaoService.editarEmpresa(
      esteiraId,
      registro.id,
      { razaoSocialExibicao: novoNome },
      justificativa
    );
    mostrarToast(resultado.mensagem);
    carregarTudo();
  };

  // Seção 4.2 — "Permitir... Alterar a justificativa, quando necessário" (empresas excluídas).
  const confirmarJustificativaExclusao = async (novaJustificativa: string) => {
    if (!registroParaJustificativaExclusao) return;
    setSalvandoJustificativaExclusao(true);
    await CircularizacaoService.alterarJustificativaExclusao(esteiraId, registroParaJustificativaExclusao.id, novaJustificativa);
    setSalvandoJustificativaExclusao(false);
    setRegistroParaJustificativaExclusao(undefined);
    mostrarToast('Justificativa da exclusão atualizada.');
    carregarTudo();
  };

  const confirmarContato = async (dados: Omit<IContatoCircularizacao, 'id'> & { id?: string }) => {
    if (!registroParaContatos) return;
    setSalvandoContato(true);
    await CircularizacaoService.salvarContato(esteiraId, registroParaContatos.id, dados);
    setSalvandoContato(false);
    setContatoEmEdicao(undefined);
    mostrarToast('Contato salvo com sucesso.');
    const atualizado = await CircularizacaoService.obterRegistro(esteiraId, categoria, registroParaContatos.id);
    setRegistroParaContatos(atualizado);
    carregarTudo();
  };

  const excluirContato = async (contatoId: string) => {
    if (!registroParaContatos) return;
    await CircularizacaoService.excluirContato(esteiraId, registroParaContatos.id, contatoId);
    const atualizado = await CircularizacaoService.obterRegistro(esteiraId, categoria, registroParaContatos.id);
    setRegistroParaContatos(atualizado);
    carregarTudo();
  };

  const definirPrincipal = async (contatoId: string) => {
    if (!registroParaContatos) return;
    await CircularizacaoService.definirContatoPrincipal(esteiraId, registroParaContatos.id, contatoId);
    const atualizado = await CircularizacaoService.obterRegistro(esteiraId, categoria, registroParaContatos.id);
    setRegistroParaContatos(atualizado);
    carregarTudo();
  };

  const confirmarPlanejamento = async (dataPrevistaEnvio: string, prazoResposta: string, contatoResponsavelId?: string) => {
    if (!registroParaPlanejamento) return;
    setSalvandoPlanejamento(true);
    const resultado = await CircularizacaoService.definirPlanejamento(esteiraId, registroParaPlanejamento.id, {
      dataPrevistaEnvio,
      prazoResposta,
      contatoResponsavelId,
    });
    setSalvandoPlanejamento(false);
    if (resultado.sucesso) setRegistroParaPlanejamento(undefined);
    mostrarToast(resultado.mensagem);
    carregarTudo();
  };

  const prepararCarta = async (registro: IRegistroCircularizacao) => {
    setIdsPreparandoCarta((prev) => new Set(prev).add(registro.id));
    const resultado = await CircularizacaoService.prepararCarta(esteiraId, registro.id);
    setIdsPreparandoCarta((prev) => {
      const novo = new Set(prev);
      novo.delete(registro.id);
      return novo;
    });
    mostrarToast(resultado.mensagem);
    carregarTudo();
  };

  const confirmarPrazoLote = async (dados: { dataPrevistaEnvio?: string; prazoResposta?: string }) => {
    setAplicandoLote(true);
    const resultado = await CircularizacaoService.aplicarPrazoEmLote(esteiraId, Array.from(selecionados), dados);
    setAplicandoLote(false);
    setMostrarPrazoLote(false);
    setSelecionados(new Set());
    mostrarToast(resultado.mensagem);
    carregarTudo();
  };

  const confirmarExclusaoLote = async (justificativaComum: string) => {
    setExcluindoLote(true);
    await Promise.all(
      Array.from(selecionados).map((id) => CircularizacaoService.excluirEmpresa(esteiraId, id, justificativaComum))
    );
    setExcluindoLote(false);
    setMostrarExclusaoLote(false);
    mostrarToast(`${selecionados.size} empresa(s) excluída(s) da circularização.`);
    setSelecionados(new Set());
    carregarTudo();
  };

  const abrirHistorico = async () => {
    setMostrarHistorico(true);
    setCarregandoHistorico(true);
    const lista = await CircularizacaoService.listarHistorico(esteiraId, categoria);
    setHistorico(lista);
    setCarregandoHistorico(false);
  };

  const confirmarNaoAplicavel = async () => {
    if (justificativaNaoAplicavelForm.trim().length === 0) return;
    await CircularizacaoService.marcarCategoriaNaoAplicavel(esteiraId, categoria, justificativaNaoAplicavelForm.trim());
    setJustificativaNaoAplicavel(justificativaNaoAplicavelForm.trim());
    setMostrarNaoAplicavel(false);
    setJustificativaNaoAplicavelForm(JUSTIFICATIVA_PADRAO_NAO_APLICAVEL);
    mostrarToast('Categoria marcada como não aplicável.');
  };

  const reativarCategoria = async () => {
    await CircularizacaoService.reativarCategoria(esteiraId, categoria);
    setJustificativaNaoAplicavel(undefined);
    mostrarToast('Categoria reativada.');
  };

  const listaAtual = aba === 'EXCLUIDAS' ? excluidos : ativos;

  return (
    <div className='auditoria-page'>
      <div className='auditoria-header-card'>
        <div className='d-flex justify-content-between align-items-start mb-3'>
          <div>
            <button type='button' className='btn btn-sm btn-light mb-3' onClick={() => history.push('/auditoria/circularizacao', location.state)}>
              <i className='bi bi-arrow-left me-1' /> Voltar para a Central de Circularizações
            </button>
            <h2 className='auditoria-title'>{categoriaInfo.label}</h2>
            <p className='auditoria-subtitle'>{categoriaInfo.descricaoTemplate}</p>
            <p className='text-muted' style={{ fontSize: 12.5 }}>
              {empresaNome} · Período: {periodoDescricao}
            </p>
          </div>
          <div className='d-flex gap-2'>
            <button type='button' className='btn btn-sm btn-light' onClick={() => setMostrarTemplates(true)}>
              <i className='bi bi-file-earmark-text me-1' /> Modelos de Carta
            </button>
            <button type='button' className='btn btn-sm btn-light' onClick={() => setMostrarPoliticaCobranca(true)}>
              <i className='bi bi-alarm me-1' /> Política de Cobrança
            </button>
            <button type='button' className='btn btn-sm btn-light' onClick={() => setMostrarRespostasPendentes(true)}>
              <i className='bi bi-envelope-paper me-1' /> Respostas Pendentes
            </button>
            <button type='button' className='btn btn-sm btn-light' onClick={abrirHistorico}>
              <i className='bi bi-clock-history me-1' /> Histórico de Ajustes
            </button>
            {justificativaNaoAplicavel ? (
              <button type='button' className='btn btn-sm btn-light-secondary' onClick={reativarCategoria}>
                <i className='bi bi-arrow-counterclockwise me-1' /> Reativar Categoria
              </button>
            ) : (
              <button type='button' className='btn btn-sm btn-light-secondary' onClick={() => setMostrarNaoAplicavel(true)}>
                Marcar Não Aplicável
              </button>
            )}
          </div>
        </div>

        {indicadores && (
          <div className='circ-indicadores-grid mb-2'>
            <div className='circ-indicador'>
              <div className='circ-indicador-label'>Sugeridas</div>
              <div className='circ-indicador-valor'>{indicadores.empresasSugeridas}</div>
            </div>
            <div className='circ-indicador'>
              <div className='circ-indicador-label'>Incluídas Manualmente</div>
              <div className='circ-indicador-valor'>{indicadores.empresasIncluidasManualmente}</div>
            </div>
            <div className='circ-indicador'>
              <div className='circ-indicador-label'>Excluídas</div>
              <div className='circ-indicador-valor'>{indicadores.empresasExcluidas}</div>
            </div>
            <div className='circ-indicador'>
              <div className='circ-indicador-label'>Ativas</div>
              <div className='circ-indicador-valor'>{indicadores.empresasAtivas}</div>
            </div>
            <div className='circ-indicador'>
              <div className='circ-indicador-label'>Cadastros Completos</div>
              <div className='circ-indicador-valor'>{indicadores.cadastrosCompletos}</div>
            </div>
            <div className='circ-indicador circ-indicador-sucesso'>
              <div className='circ-indicador-label'>Cartas Preparadas</div>
              <div className='circ-indicador-valor'>{indicadores.cartasPreparadas}</div>
            </div>
            <div className='circ-indicador circ-indicador-critico'>
              <div className='circ-indicador-label'>Cartas Pendentes</div>
              <div className='circ-indicador-valor'>{indicadores.cartasPendentes}</div>
            </div>
            <div className={indicadores.prazosVencidos > 0 ? 'circ-indicador circ-indicador-critico' : 'circ-indicador'}>
              <div className='circ-indicador-label'>Prazos Vencidos</div>
              <div className='circ-indicador-valor'>{indicadores.prazosVencidos}</div>
            </div>
            <div className='circ-indicador'>
              <div className='circ-indicador-label'>% Preparação</div>
              <div className='circ-indicador-valor'>
                {indicadores.percentualPreparacao === null ? '—' : `${indicadores.percentualPreparacao}%`}
              </div>
            </div>
          </div>
        )}
      </div>

      {justificativaNaoAplicavel && (
        <div className='circ-aviso-revisao mb-3' style={{ background: '#eef0f4', borderColor: '#d7dce5', color: '#47586b' }}>
          <i className='bi bi-slash-circle' />
          <div>
            <strong>Categoria marcada como não aplicável.</strong> {justificativaNaoAplicavel}
          </div>
        </div>
      )}

      {/* Seção 12.8 — aviso obrigatório sobre a revisão da seleção. */}
      <div className='circ-aviso-revisao mb-3'>
        <i className='bi bi-info-circle-fill' />
        <div>
          Esta relação contém destinatários preliminarmente selecionados para circularização. Revise as
          informações, efetue os ajustes necessários e confirme a seleção antes de preparar as cartas.
        </div>
      </div>

      {indicadores && (
        <CriteriosConclusaoPanel
          ativos={ativos}
          excluidos={excluidos}
          indicadores={indicadores}
          categoriaMarcadaNaoAplicavel={!!justificativaNaoAplicavel}
        />
      )}

      <AlertasInconsistenciaPanel alertas={alertas} registros={ativos} />

      {/* Seção 3.1 — nome literal da funcionalidade obrigatória do documento. */}
      <div className='circ-card'>
        <h3 style={{ fontSize: 14, marginBottom: 12 }}>Empresas Selecionadas para Circularização</h3>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <div className='circ-tabs'>
            <button type='button' className={`circ-tab ${aba === 'ATIVAS' ? 'circ-tab-ativa' : ''}`} onClick={() => setAba('ATIVAS')}>
              Empresas Ativas ({ativos.length})
            </button>
            <button type='button' className={`circ-tab ${aba === 'EXCLUIDAS' ? 'circ-tab-ativa' : ''}`} onClick={() => setAba('EXCLUIDAS')}>
              Empresas Excluídas ({excluidos.length})
            </button>
            <button type='button' className={`circ-tab ${aba === 'PLANEJAMENTO' ? 'circ-tab-ativa' : ''}`} onClick={() => setAba('PLANEJAMENTO')}>
              Planejamento
            </button>
          </div>
          <button type='button' className='btn btn-sm btn-primary' onClick={() => setMostrarIncluir(true)}>
            <i className='bi bi-plus-lg me-1' /> Incluir Empresa
          </button>
        </div>

        {aba === 'ATIVAS' && selecionados.size > 0 && (
          <div className='d-flex align-items-center gap-2 mb-3' style={{ background: '#eaf3fa', padding: '8px 12px', borderRadius: 8 }}>
            <span style={{ fontSize: 12.5 }}>{selecionados.size} selecionada(s)</span>
            <button type='button' className='btn btn-sm btn-light-primary' onClick={() => setMostrarPrazoLote(true)}>
              Aplicar Prazo em Lote
            </button>
            <button type='button' className='btn btn-sm btn-light-primary' onClick={() => setMostrarImpressaoLote(true)}>
              <i className='bi bi-printer me-1' /> Imprimir Selecionadas
            </button>
            <button type='button' className='btn btn-sm btn-light-danger' onClick={() => setMostrarExclusaoLote(true)}>
              Excluir Selecionadas
            </button>
          </div>
        )}

        {aba === 'PLANEJAMENTO' ? (
          <PlanejamentoCircularizacaoTable registros={ativos} onAbrirPlanejamento={setRegistroParaPlanejamento} />
        ) : (
          <>
            {carregando && <div className='text-muted text-center py-5'>Carregando...</div>}

            {!carregando && listaAtual.length === 0 && (
              <div className='circ-empty-state'>
                <i className='bi bi-inbox circ-empty-state-icon' />
                <div className='circ-empty-state-title'>
                  {aba === 'ATIVAS' ? 'Nenhuma empresa ativa nesta categoria' : 'Nenhuma empresa excluída nesta categoria'}
                </div>
                {aba === 'ATIVAS' && (
                  <div className='circ-empty-state-texto'>Adicione uma nova empresa para continuar com a circularização.</div>
                )}
              </div>
            )}

            {!carregando && listaAtual.length > 0 && (
              <div className='table-responsive'>
                <table className='table table-sm table-row-dashed table-row-gray-200 align-middle gs-0 gy-2 fs-8'>
                  <thead>
                    <tr className='fw-bolder text-muted'>
                      <th style={{ width: 32 }}>
                        {aba === 'ATIVAS' && (
                          <input
                            type='checkbox'
                            className='form-check-input'
                            aria-label='Selecionar todas'
                            checked={selecionados.size > 0 && selecionados.size === ativos.length}
                            onChange={selecionarTodos}
                          />
                        )}
                      </th>
                      <th>Empresa / Instituição</th>
                      <th>CPF/CNPJ</th>
                      <th>Categoria</th>
                      <th>Origem</th>
                      <th>Justificativa</th>
                      <th>Situação</th>
                      <th>Carta</th>
                      <th className='text-end'>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listaAtual.map((registro) => (
                      <RegistroCircularizacaoRow
                        key={registro.id}
                        registro={registro}
                        modoExcluidos={aba === 'EXCLUIDAS'}
                        selecionado={selecionados.has(registro.id)}
                        preparandoCarta={idsPreparandoCarta.has(registro.id)}
                        onToggleSelecionado={toggleSelecionado}
                        onEditar={setRegistroParaEditar}
                        onRenomearRapido={confirmarRenomeioRapido}
                        onExcluir={setRegistroParaExcluir}
                        onRestaurar={confirmarRestaurar}
                        onEditarJustificativaExclusao={setRegistroParaJustificativaExclusao}
                        onConsultarJustificativa={setRegistroParaJustificativa}
                        onGerenciarContatos={setRegistroParaContatos}
                        onAbrirPlanejamento={setRegistroParaPlanejamento}
                        onPrepararCarta={prepararCarta}
                        onPreVisualizarCarta={setRegistroParaPreview}
                        onAbrirCarta={setRegistroParaCarta}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {mostrarIncluir && (
        <IncluirEmpresaModal
          categoria={categoria}
          categoriaLabel={categoriaInfo.label}
          salvando={salvandoIncluir}
          onCancelar={() => setMostrarIncluir(false)}
          onConfirmar={confirmarIncluir}
        />
      )}

      {registroParaExcluir && (
        <ExcluirEmpresaModal
          registro={registroParaExcluir}
          categoriaLabel={categoriaInfo.label}
          excluindo={excluindo}
          onCancelar={() => setRegistroParaExcluir(undefined)}
          onConfirmar={confirmarExcluir}
        />
      )}

      {registroParaEditar && (
        <EditarEmpresaModal
          registro={registroParaEditar}
          salvando={salvandoEdicao}
          onCancelar={() => setRegistroParaEditar(undefined)}
          onConfirmar={confirmarEditar}
        />
      )}

      {registroParaJustificativaExclusao && (
        <EditarJustificativaExclusaoModal
          registro={registroParaJustificativaExclusao}
          salvando={salvandoJustificativaExclusao}
          onCancelar={() => setRegistroParaJustificativaExclusao(undefined)}
          onConfirmar={confirmarJustificativaExclusao}
        />
      )}

      {registroParaJustificativa && (
        <JustificativaModal registro={registroParaJustificativa} onFechar={() => setRegistroParaJustificativa(undefined)} />
      )}

      {registroParaContatos && !contatoEmEdicao && (
        <div className='circ-modal-backdrop' onMouseDown={(e) => e.target === e.currentTarget && setRegistroParaContatos(undefined)}>
          <div className='circ-modal'>
            <div className='circ-modal-header'>
              <h4>Contatos — {registroParaContatos.razaoSocialExibicao}</h4>
              <button type='button' className='btn btn-sm btn-icon btn-light' aria-label='Fechar' onClick={() => setRegistroParaContatos(undefined)}>
                <i className='bi bi-x-lg' />
              </button>
            </div>
            <div className='circ-modal-body'>
              {registroParaContatos.contatos.length === 0 && (
                <div className='text-muted' style={{ fontSize: 13 }}>Nenhum contato cadastrado ainda.</div>
              )}
              {registroParaContatos.contatos.map((c) => (
                <div key={c.id} className='circ-contato-item'>
                  <div className='circ-contato-info'>
                    <div className='circ-contato-nome'>
                      {c.nomeCompleto} {c.contatoPrincipal && <span className='badge badge-light-primary ms-1'>Principal</span>}
                    </div>
                    <div className='circ-contato-meta'>{c.emailPrincipal} · {c.telefone}</div>
                  </div>
                  <div className='d-flex gap-1'>
                    {!c.contatoPrincipal && (
                      <button type='button' className='btn btn-sm btn-light' onClick={() => definirPrincipal(c.id)}>
                        Definir Principal
                      </button>
                    )}
                    <button type='button' className='btn btn-sm btn-light' onClick={() => setContatoEmEdicao(c)}>
                      <i className='bi bi-pencil' />
                    </button>
                    <button type='button' className='btn btn-sm btn-light-danger' onClick={() => excluirContato(c.id)}>
                      <i className='bi bi-trash' />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className='circ-modal-footer'>
              <button type='button' className='btn btn-sm btn-light' onClick={() => setRegistroParaContatos(undefined)}>
                Fechar
              </button>
              <button type='button' className='btn btn-sm btn-primary' onClick={() => setContatoEmEdicao('novo')}>
                <i className='bi bi-plus-lg me-1' /> Novo Contato
              </button>
            </div>
          </div>
        </div>
      )}

      {registroParaContatos && contatoEmEdicao && (
        <ContatoFormModal
          contato={contatoEmEdicao === 'novo' ? undefined : contatoEmEdicao}
          salvando={salvandoContato}
          onCancelar={() => setContatoEmEdicao(undefined)}
          onConfirmar={confirmarContato}
        />
      )}

      {registroParaPlanejamento && (
        <div className='circ-modal-backdrop' onMouseDown={(e) => e.target === e.currentTarget && setRegistroParaPlanejamento(undefined)}>
          <div className='circ-modal' style={{ maxWidth: 460 }}>
            <div className='circ-modal-header'>
              <h4>Planejar Envio e Resposta</h4>
              <button type='button' className='btn btn-sm btn-icon btn-light' aria-label='Fechar' onClick={() => setRegistroParaPlanejamento(undefined)}>
                <i className='bi bi-x-lg' />
              </button>
            </div>
            <PlanejamentoIndividualForm
              registro={registroParaPlanejamento}
              salvando={salvandoPlanejamento}
              onCancelar={() => setRegistroParaPlanejamento(undefined)}
              onConfirmar={confirmarPlanejamento}
            />
          </div>
        </div>
      )}

      {registroParaPreview && (
        <ModeloCartaPreviewModal
          registro={registroParaPreview}
          empresaAuditada={empresaNome}
          dataBase={location.state?.periodo ?? ''}
          emailRetornoEquipe='auditoria@ainumera.com.br'
          nomeSignatario='Equipe de Auditoria AI Númera'
          onFechar={() => setRegistroParaPreview(undefined)}
          onImprimir={() => window.print()}
        />
      )}

      {mostrarPrazoLote && (
        <PlanejamentoLoteModal
          quantidadeSelecionada={selecionados.size}
          aplicando={aplicandoLote}
          onCancelar={() => setMostrarPrazoLote(false)}
          onConfirmar={confirmarPrazoLote}
        />
      )}

      {mostrarExclusaoLote && (
        <ExclusaoEmLoteModal
          quantidadeSelecionada={selecionados.size}
          excluindo={excluindoLote}
          onCancelar={() => setMostrarExclusaoLote(false)}
          onConfirmar={confirmarExclusaoLote}
        />
      )}

      {mostrarImpressaoLote && (
        <ImpressaoEmLoteModal
          registros={ativos.filter((r) => selecionados.has(r.id))}
          empresaAuditada={empresaNome}
          dataBase={location.state?.periodo ?? ''}
          emailRetornoEquipe='auditoria@ainumera.com.br'
          nomeSignatario='Equipe de Auditoria AI Númera'
          onFechar={() => setMostrarImpressaoLote(false)}
        />
      )}

      {mostrarHistorico && (
        <HistoricoAjustesModal historico={historico} carregando={carregandoHistorico} onFechar={() => setMostrarHistorico(false)} />
      )}

      {mostrarNaoAplicavel && (
        <div className='circ-modal-backdrop' onMouseDown={(e) => e.target === e.currentTarget && setMostrarNaoAplicavel(false)}>
          <div className='circ-modal' style={{ maxWidth: 460 }}>
            <div className='circ-modal-header'>
              <h4>Marcar Categoria como Não Aplicável</h4>
              <button type='button' className='btn btn-sm btn-icon btn-light' aria-label='Fechar' onClick={() => setMostrarNaoAplicavel(false)}>
                <i className='bi bi-x-lg' />
              </button>
            </div>
            <div className='circ-modal-body'>
              <div className='circ-form-group'>
                <label className='circ-campo-obrigatorio'>Justificativa</label>
                <textarea
                  className='circ-form-control'
                  rows={3}
                  value={justificativaNaoAplicavelForm}
                  onChange={(e) => setJustificativaNaoAplicavelForm(e.target.value)}
                  placeholder='Ex.: Não há partes relacionadas identificadas no exercício em auditoria.'
                />
              </div>
            </div>
            <div className='circ-modal-footer'>
              <button type='button' className='btn btn-sm btn-light' onClick={() => setMostrarNaoAplicavel(false)}>
                Cancelar
              </button>
              <button
                type='button'
                className='btn btn-sm btn-primary'
                disabled={justificativaNaoAplicavelForm.trim().length === 0}
                onClick={confirmarNaoAplicavel}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {registroParaCarta && (
        <CartaDetalheModal registro={registroParaCarta} onFechar={() => setRegistroParaCarta(undefined)} />
      )}

      {mostrarTemplates && (
        <TemplatesModal categoria={categoria} onFechar={() => setMostrarTemplates(false)} />
      )}

      {mostrarPoliticaCobranca && (
        <PoliticaCobrancaModal
          esteiraId={esteiraId}
          categoria={categoria}
          categoriaLabel={categoriaInfo.label}
          onFechar={() => setMostrarPoliticaCobranca(false)}
        />
      )}

      {mostrarRespostasPendentes && (
        <RespostasPendentesModal esteiraId={esteiraId} onFechar={() => setMostrarRespostasPendentes(false)} />
      )}

      {toast && <div className='circ-toast'>{toast}</div>}
    </div>
  );
};

// Formulário simples de planejamento individual (data de envio + prazo de resposta +
// contato responsável) — seção 6/7 do documento.
const PlanejamentoIndividualForm: React.FC<{
  registro: IRegistroCircularizacao;
  salvando: boolean;
  onCancelar: () => void;
  onConfirmar: (dataPrevistaEnvio: string, prazoResposta: string, contatoResponsavelId?: string) => void;
}> = ({ registro, salvando, onCancelar, onConfirmar }) => {
  const [dataPrevistaEnvio, setDataPrevistaEnvio] = useState(registro.dataPrevistaEnvio ?? '');
  const [prazoResposta, setPrazoResposta] = useState(registro.prazoResposta ?? '');
  const [contatoResponsavelId, setContatoResponsavelId] = useState(registro.contatoResponsavelId ?? '');
  const [erro, setErro] = useState<string | undefined>();

  const confirmar = () => {
    if (!dataPrevistaEnvio || !prazoResposta) {
      setErro('Data prevista de envio e prazo para resposta são obrigatórios.');
      return;
    }
    if (prazoResposta < dataPrevistaEnvio) {
      setErro('O prazo para resposta não pode ser anterior à data prevista de envio.');
      return;
    }
    setErro(undefined);
    onConfirmar(dataPrevistaEnvio, prazoResposta, contatoResponsavelId || undefined);
  };

  return (
    <>
      <div className='circ-modal-body'>
        <div className='circ-form-group'>
          <label htmlFor='circ-planejamento-data-envio' className='circ-campo-obrigatorio'>Data Prevista de Envio</label>
          <input id='circ-planejamento-data-envio' type='date' className='circ-form-control' value={dataPrevistaEnvio} onChange={(e) => setDataPrevistaEnvio(e.target.value)} />
        </div>
        <div className='circ-form-group'>
          <label htmlFor='circ-planejamento-prazo-resposta' className='circ-campo-obrigatorio'>Prazo para Resposta</label>
          <input id='circ-planejamento-prazo-resposta' type='date' className='circ-form-control' value={prazoResposta} onChange={(e) => setPrazoResposta(e.target.value)} />
        </div>
        {registro.contatos.length > 0 && (
          <div className='circ-form-group'>
            <label htmlFor='circ-planejamento-contato'>Contato Responsável</label>
            <select id='circ-planejamento-contato' className='circ-form-control' value={contatoResponsavelId} onChange={(e) => setContatoResponsavelId(e.target.value)}>
              <option value=''>Selecione...</option>
              {registro.contatos.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nomeCompleto}
                </option>
              ))}
            </select>
          </div>
        )}
        {erro && <div className='text-danger' style={{ fontSize: 12 }}>{erro}</div>}
      </div>
      <div className='circ-modal-footer'>
        <button type='button' className='btn btn-sm btn-light' disabled={salvando} onClick={onCancelar}>
          Cancelar
        </button>
        <button type='button' className='btn btn-sm btn-primary' disabled={salvando} onClick={confirmar}>
          {salvando && <span className='spinner-border spinner-border-sm me-1' />}
          Salvar Planejamento
        </button>
      </div>
    </>
  );
};

export default CategoriaCircularizacaoPage;
